<script setup lang="ts">
import IcClose from '@/components/Icons/IcClose.vue'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface VisibleFilters {
  description: boolean
  origin: boolean
  maintenance_type: boolean
  actions_taken: boolean
  scheduled_period: boolean
}

interface Props {
  modelValue: boolean
  visibleFilters: VisibleFilters
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:visibleFilters', value: VisibleFilters): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const localFilters = ref<VisibleFilters>({
  description: props.visibleFilters.description || false,
  origin: props.visibleFilters.origin || false,
  maintenance_type: props.visibleFilters.maintenance_type || false,
  actions_taken: props.visibleFilters.actions_taken || false,
  scheduled_period: props.visibleFilters.scheduled_period || false
})

watch(
  () => props.visibleFilters,
  (newFilters) => {
    localFilters.value = { ...newFilters }
  },
  { deep: true, immediate: true },
)

const closeModal = () => {
  emit('update:modelValue', false)
}

const applyFilters = () => {
  emit('update:visibleFilters', localFilters.value)
  closeModal()
}
</script>

<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="relative w-full max-w-md bg-white p-6 shadow-xl">
      <div class="mb-4 flex items-center justify-between">
        <div class="flex items-center">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ t('maintenance_listing.header.filters_modal.title') }}
          </h3>
        </div>
        <button @click="closeModal" class="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <IcClose class="h-5 w-5" />
        </button>
      </div>

      <div class="space-y-4">
        <p class="text-gray-500 mb-4">{{ t('maintenance_listing.header.filters_modal.subtitle') }}</p>

        <div class="space-y-2">
          <div class="flex items-center p-2 hover:bg-gray-50">
            <input 
              type="checkbox" 
              id="filter-description" 
              v-model="localFilters.description"
              class="mr-3 h-4 w-4 cursor-pointer accent-blue"
            />
            <label for="filter-description" class="cursor-pointer select-none">
              {{ t('maintenance_listing.body.table.columns.description') }}
            </label>
          </div>
          
          <div class="flex items-center p-2 hover:bg-gray-50">
            <input 
              type="checkbox" 
              id="filter-origin" 
              v-model="localFilters.origin"
              class="mr-3 h-4 w-4 cursor-pointer accent-blue"
            />
            <label for="filter-origin" class="cursor-pointer select-none">
              {{ t('maintenance_listing.body.table.columns.origin') }}
            </label>
          </div>
          
          <div class="flex items-center p-2 hover:bg-gray-50">
            <input 
              type="checkbox" 
              id="filter-maintenance_type" 
              v-model="localFilters.maintenance_type"
              class="mr-3 h-4 w-4 cursor-pointer accent-blue"
            />
            <label for="filter-maintenance_type" class="cursor-pointer select-none">
              {{ t('maintenance_listing.body.table.columns.maintenance_type') }}
            </label>
          </div>
          
          <div class="flex items-center p-2 hover:bg-gray-50">
            <input 
              type="checkbox" 
              id="filter-actions_taken" 
              v-model="localFilters.actions_taken"
              class="mr-3 h-4 w-4 cursor-pointer accent-blue"
            />
            <label for="filter-actions_taken" class="cursor-pointer select-none">
              {{ t('maintenance_listing.body.table.columns.actions_taken') }}
            </label>
          </div>
          
          <div class="flex items-center p-2 hover:bg-gray-50">
            <input 
              type="checkbox" 
              id="filter-scheduled_period" 
              v-model="localFilters.scheduled_period"
              class="mr-3 h-4 w-4 cursor-pointer accent-blue"
            />
            <label for="filter-scheduled_period" class="cursor-pointer select-none">
              {{ t('maintenance_listing.body.table.columns.scheduled_for') }}
            </label>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-end">
        <div class="flex space-x-3">
          <button 
            @click="closeModal" 
            class="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {{ t('common.cancel') || 'Cancelar' }}
          </button>
          <button 
            @click="applyFilters" 
            class="bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-2">
            {{ t('maintenance_listing.header.filters_modal.buttons.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>